'use client'

import { Fragment } from 'react'

import { NestedFigsThemeProvider } from '@syconium/little-miss-figgy'
import { Plus } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/Plus'
import { lgEndPixels, smEndPixels } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { gql } from '../../../../../__generated__/graphql/catalog/gql'
import { PageBannerSectionFragmentFragment } from '../../../../../__generated__/graphql/catalog/graphql'
import { trackEvent } from '../../../../../lib/analytics'
import { useTranslation } from '../../../../_providers/TranslationProvider.client'
import { ClientOnlyResponsiveVideo } from '../../../media/ClientOnlyResponsiveVideo.client'
import { WatchVideoDialog } from '../../../media/WatchVideoDialog.client'
import { NextLink } from '../../../navigation/NextLink'
import { PageSection, PageSectionContent } from '../../PageSection.client'
import { ContentfulRichText } from '../components/ContentfulRichText'
import { ContentfulStyledImage } from '../components/ContentfulStyledImage'
import { ContentfulStyledText } from '../components/ContentfulStyledText'
import { getContentfulPageSectionAnalyticsName } from '../ContentfulPageSectionProvider.utils'

import {
  ContentfulPageBannerSectionAssetOverlayedContent,
  ContentfulPageBannerSectionAssetWrapper,
  ContentfulPageBannerSectionBackground,
  ContentfulPageBannerSectionContent,
  ContentfulPageBannerSectionHotspot,
  ContentfulPageBannerSectionVideoDialogButtonWrapper,
  Disclaimer,
} from './ContentfulPageBannerSection.styles'

export const pageBannerSectionFragment = gql(`
  fragment PageBannerSectionFragment on PageBannerSection {
    __typename  
    id
    name
    backgroundColorDesktop
    backgroundColorMobile
    disclaimer
    hasVerticalMargin
    hotspot {
      linkHref1
    }
    backgroundImage {
      ...StyledImageFragment
    }
    inlineVideoDesktop
    inlineVideoMobile
    inlineVimeoIdDesktop
    inlineVimeoIdMobile
    isFullBleedDesktop
    modalVideoId
    modalVideoPlatform
    openVideoModalText
    overlayColorDesktop
    overlayColorMobile
    textPlacementDesktop
    textPlacementMobile
    styledContentItems {
      __typename
      ... on StyledText {
        ...StyledTextFragment
      }
      ... on StyledImage {
        ...StyledImageFragment
      }
    }
  }
`)

export type ContentfulPageBannerSectionProps = {
  pageSectionIndex: number
  data: PageBannerSectionFragmentFragment
}

export const ContentfulPageBannerSection = ({
  data,
  pageSectionIndex,
}: ContentfulPageBannerSectionProps) => {
  const translations = useTranslation()

  const playText = translations.magnolia.general.playVideo
  const pauseText = translations.magnolia.general.pauseVideo

  const section = data
  const isFullBleedDesktop = section.isFullBleedDesktop ?? false
  const hasVerticalMargin = section.hasVerticalMargin ?? true
  const name = section.name ?? undefined
  const textPlacementDesktop = section.textPlacementDesktop ?? undefined
  const textPlacementMobile = section.textPlacementMobile ?? undefined
  const backgroundColorDesktop = section.backgroundColorDesktop ?? undefined
  const backgroundColorMobile = section.backgroundColorMobile ?? undefined
  const styledContentItems = section.styledContentItems
  const lastContentItem = styledContentItems.at(-1)
  const overlayColorDesktop = section.overlayColorDesktop
  const overlayColorMobile = section.overlayColorMobile
  const hotspot = section.hotspot
  const modalVideoId = section.modalVideoId
  const modalVideoPlatform = section.modalVideoPlatform
  const openVideoModalText = section.openVideoModalText
  const disclaimer = section.disclaimer

  const inlineVideoDesktop = section.inlineVideoDesktop
  const inlineVideoMobile = section.inlineVideoMobile
  const inlineVimeoIdDesktop = section.inlineVimeoIdDesktop
  const inlineVimeoIdMobile = section.inlineVimeoIdMobile

  const backgroundImage = section.backgroundImage

  const backgroundImageThemeDesktop = section.backgroundImage?.imageThemeDesktop
  const backgroundImageThemeMobile = section.backgroundImage?.imageThemeMobile

  const shouldShowHotspot =
    Boolean(hotspot?.linkHref1) &&
    (textPlacementDesktop === 'right' ||
      textPlacementDesktop === 'left' ||
      textPlacementMobile === 'below')

  const isStyledText = (
    styledContentItem: (typeof styledContentItems)[number]
  ): styledContentItem is Extract<
    (typeof styledContentItems)[number],
    { __typename: 'StyledText' }
  > => {
    return styledContentItem.__typename === 'StyledText'
  }

  const isStyledImage = (
    styledContentItem: (typeof styledContentItems)[number]
  ): styledContentItem is Extract<
    (typeof styledContentItems)[number],
    { __typename: 'StyledImage' }
  > => {
    return styledContentItem.__typename === 'StyledImage'
  }

  const DisclaimerText = (
    <NestedFigsThemeProvider
      themeDesktop={textPlacementDesktop === 'center' ? backgroundImageThemeDesktop : 'default'}
      themeMobile={textPlacementMobile === 'below' ? 'default' : backgroundImageThemeMobile}
    >
      <Disclaimer $placement={textPlacementDesktop}>
        <ContentfulRichText content={disclaimer} bodySize='BodyTiny' fontWeight='600' />
      </Disclaimer>
    </NestedFigsThemeProvider>
  )

  const analyticsContentfulName = name
  const analyticsPageSectionName = styledContentItems.find(isStyledText)?.title ?? name
  const analyticsCategory = getContentfulPageSectionAnalyticsName(section.__typename)
  const analyticsAsset =
    backgroundImage?.imageDesktop ?? styledContentItems.find(isStyledImage)?.imageDesktop
  return (
    <PageSection
      hasVerticalMargin={hasVerticalMargin}
      {...trackEvent({
        category: analyticsCategory,
        action: 'any',
        pageSectionName: analyticsPageSectionName,
        pageSectionIndex,
        contentfulName: analyticsContentfulName,
        correspondingAsset: analyticsAsset ?? undefined,
      })}
    >
      <PageSectionContent
        gutterSize={{
          sm: 'none',
          md: isFullBleedDesktop ? 'none' : 'expanded',
        }}
      >
        <ContentfulPageBannerSectionBackground
          data-testid='page-banner-section-body'
          $backgroundColorDesktop={backgroundColorDesktop}
          $backgroundColorMobile={backgroundColorMobile}
          $textPlacementDesktop={textPlacementDesktop}
          $textPlacementMobile={textPlacementMobile}
        >
          <ContentfulPageBannerSectionAssetWrapper
            $isFullBleedDesktop={isFullBleedDesktop}
            $isTallAssetMobile={Boolean(inlineVideoMobile)}
            $overlayColorDesktop={overlayColorDesktop}
            $overlayColorMobile={overlayColorMobile}
            $textPlacementDesktop={textPlacementDesktop}
            $textPlacementMobile={textPlacementMobile}
          >
            {backgroundImage ? (
              <ContentfulStyledImage
                {...backgroundImage}
                aria-hidden='true'
                widths={{
                  unit: 'vw',
                  sm: 100,
                  md: textPlacementDesktop === 'center' ? 100 : 50,
                }}
                loading={pageSectionIndex === 0 ? 'default' : 'lazy'}
                fetchPriority={pageSectionIndex === 0 ? 'high' : 'auto'}
              />
            ) : null}

            <ClientOnlyResponsiveVideo
              playText={playText}
              pauseText={pauseText}
              inlineVideoDesktop={inlineVideoDesktop}
              inlineVideoMobile={inlineVideoMobile}
              inlineVimeoIdDesktop={inlineVimeoIdDesktop}
              inlineVimeoIdMobile={inlineVimeoIdMobile}
            />

            <NestedFigsThemeProvider
              themeDesktop={backgroundImageThemeDesktop}
              themeMobile={backgroundImageThemeMobile}
            >
              <ContentfulPageBannerSectionAssetOverlayedContent>
                {modalVideoId && modalVideoPlatform ? (
                  <ContentfulPageBannerSectionVideoDialogButtonWrapper
                    $location='asset'
                    $spacing={openVideoModalText ? 'less' : 'more'}
                    $textPlacementMobile={textPlacementMobile}
                    $forceDisplay={styledContentItems.length === 0}
                    className='configurable-width'
                  >
                    <WatchVideoDialog
                      videoId={modalVideoId}
                      videoPlatform={modalVideoPlatform}
                      text={openVideoModalText}
                    />
                  </ContentfulPageBannerSectionVideoDialogButtonWrapper>
                ) : null}
              </ContentfulPageBannerSectionAssetOverlayedContent>

              {shouldShowHotspot && hotspot?.linkHref1 ? (
                <ContentfulPageBannerSectionHotspot as={NextLink} href={hotspot.linkHref1}>
                  <Plus />
                </ContentfulPageBannerSectionHotspot>
              ) : null}
            </NestedFigsThemeProvider>
          </ContentfulPageBannerSectionAssetWrapper>

          <NestedFigsThemeProvider
            themeDesktop={
              textPlacementDesktop === 'center' ? backgroundImageThemeDesktop : 'default'
            }
            themeMobile={textPlacementMobile === 'below' ? 'default' : backgroundImageThemeMobile}
          >
            {styledContentItems.length > 0 && (
              <ContentfulPageBannerSectionContent
                $textPlacementDesktop={textPlacementDesktop}
                $textPlacementMobile={textPlacementMobile}
                $hasDisclaimer={!!disclaimer}
                $endsInLink={
                  lastContentItem?.__typename === 'StyledText' && lastContentItem?.links.length > 0
                }
              >
                {styledContentItems.map((styledContentItem, index) => {
                  const key = `${styledContentItem.id}-${index}`
                  const isLastStyledItem = index === styledContentItems.length - 1

                  const additionalContent =
                    isLastStyledItem && modalVideoId && modalVideoPlatform ? (
                      <ContentfulPageBannerSectionVideoDialogButtonWrapper
                        $location='text'
                        $spacing={openVideoModalText ? 'less' : 'more'}
                        $textPlacementMobile={textPlacementMobile}
                        $forceDisplay={false}
                        className='configurable-width'
                      >
                        <WatchVideoDialog
                          videoId={modalVideoId}
                          videoPlatform={modalVideoPlatform}
                          text={openVideoModalText}
                        />
                      </ContentfulPageBannerSectionVideoDialogButtonWrapper>
                    ) : null

                  if (styledContentItem.__typename === 'StyledText') {
                    return (
                      <ContentfulStyledText
                        key={key}
                        {...styledContentItem}
                        centerFloatContent
                        defaultMaxWidth={450}
                        defaultBottomMargin={index < styledContentItems.length - 1 ? 24 : 0}
                        additionalCustomContent={additionalContent}
                      />
                    )
                  } else if (styledContentItem.__typename === 'StyledImage') {
                    return (
                      <Fragment key={key}>
                        <ContentfulStyledImage
                          key={key}
                          {...styledContentItem}
                          loading='lazy'
                          transparentBackground
                          widths={
                            styledContentItem.maxWidthMobile || styledContentItem.maxWidthDesktop
                              ? {
                                  unit: 'px',
                                  sm: styledContentItem.maxWidthMobile ?? smEndPixels,
                                  md: styledContentItem.maxWidthDesktop ?? lgEndPixels,
                                }
                              : {
                                  unit: 'vw',
                                  md: textPlacementDesktop === 'center' ? 100 : 50,
                                  sm: 100,
                                }
                          }
                        />
                        {additionalContent}
                      </Fragment>
                    )
                  } else {
                    return null
                  }
                })}
                {textPlacementDesktop !== 'center' && DisclaimerText}
              </ContentfulPageBannerSectionContent>
            )}
          </NestedFigsThemeProvider>
        </ContentfulPageBannerSectionBackground>
      </PageSectionContent>
      {textPlacementDesktop === 'center' && DisclaimerText}
    </PageSection>
  )
}
